import { Component, ElementRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { TourService } from '../../tour/tour.service';
import { TourId } from '../../tour/tour-definitions';
import { Subscription, filter } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBell, faBellSlash, faRobot } from '@fortawesome/free-solid-svg-icons';
import { BreakyComponent } from '../breaky/breaky.component';
import { BreakyCompanionService } from '../../../services/breaky-companion.service';
import { BreakyWardrobeService } from '../../../services/breaky-wardrobe.service';
import { CompanionAction } from '../../breaky/breaky-companion.rules';
import { isCompanionHidden } from './companion-visibility';

const POS_KEY = 'sb_companion_pos';
const SIZE = 76;

/**
 * Breaky flotante: una mascota que acompana en toda la app. Se arrastra a un borde, habla poco y al tocarla
 * abre un menu rapido (pausa, preguntarle, su armario, silenciarlo).
 */
@Component({
  selector: 'app-breaky-companion',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, BreakyComponent],
  template: `
    <div
      class="bc"
      *ngIf="shown && (companion.prefs$ | async)?.visible"
      [class.bc--left]="side === 'left'"
      [style.bottom.px]="bottom"
      [style.left.px]="side === 'left' ? 8 : null"
      [style.right.px]="side === 'right' ? 8 : null"
      [style.transform]="dragging ? 'translate(' + dragDx + 'px,' + dragDy + 'px)' : null"
    >
      <div class="bc-bubble" *ngIf="companion.message$ | async as m" role="status">
        <button type="button" class="bc-x" aria-label="Cerrar mensaje" (click)="companion.dismiss()">×</button>
        <p>{{ m.text }}</p>
        <div class="bc-actions" *ngIf="m.actions?.length">
          <button *ngFor="let a of m.actions; let first = first" type="button" class="bc-act" [class.bc-act--main]="first && (a.route || a.tour)" (click)="act(a)">{{ a.label }}</button>
        </div>
      </div>

      <div class="bc-menu" *ngIf="menuOpen" role="menu">
        <div class="bc-menu-bar" *ngIf="companion.prefs$ | async as prefs">
          <button type="button" class="bc-ico" aria-label="Mi Breaky" title="Mi Breaky" (click)="go('/dashboard/mi-breaky')">
            <fa-icon [icon]="faRobot"></fa-icon>
          </button>
          <button
            type="button"
            class="bc-ico"
            [class.bc-ico--off]="!prefs.messages"
            [attr.aria-pressed]="!prefs.messages"
            [attr.aria-label]="prefs.messages ? 'Silenciar mensajes' : 'Activar mensajes'"
            [title]="prefs.messages ? 'Silenciar mensajes' : 'Mensajes silenciados: toca para activarlos'"
            (click)="toggleMessages()"
          >
            <fa-icon [icon]="prefs.messages ? faBell : faBellSlash"></fa-icon>
          </button>
        </div>
        <button type="button" role="menuitem" (click)="go('/dashboard/pausas')">⏸ Hacer una pausa</button>
        <button type="button" role="menuitem" (click)="tip()">💡 Dame un consejo</button>
        <button type="button" role="menuitem" (click)="go('/dashboard/ayuda?tab=breaky')">💬 Preguntarle a Breaky</button>
      </div>

      <div
        class="bc-avatar"
        data-tour="breaky"
        (pointerdown)="down($event)"
        (pointermove)="move($event)"
        (pointerup)="up($event)"
        (pointercancel)="cancel()"
        [style.width.px]="size"
        [style.height.px]="size"
        aria-label="Breaky"
        role="button"
      >
        <app-breaky [pose]="(companion.message$ | async)?.pose ?? (companion.restPose$ | async) ?? 'idle'" [size]="size" [accessories]="outfit$ | async"></app-breaky>
        <span class="bc-dot" *ngIf="(companion.message$ | async) && !menuOpen" aria-hidden="true"></span>
      </div>
    </div>
  `,
  styles: [
    `
      .bc { position: fixed; z-index: 900; display: flex; flex-direction: column; align-items: flex-end; gap: 6px; max-width: min(300px, calc(100vw - 16px)); touch-action: none; }
      .bc--left { align-items: flex-start; }
      .bc-avatar { position: relative; touch-action: none; cursor: grab; filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.45)); }
      .bc-avatar:active { cursor: grabbing; }
      .bc-dot { position: absolute; top: 2px; right: 6px; width: 12px; height: 12px; border-radius: 50%; background: #a6ff00; box-shadow: 0 0 10px #a6ff00; animation: bc-pulse 1.4s ease-in-out infinite; }
      @keyframes bc-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.35); } }
      .bc-bubble { position: relative; padding: 12px 30px 12px 14px; border-radius: 18px 18px 6px 18px; color: #fff; background: linear-gradient(160deg, #1d3a73, #0f1d45); border: 1px solid #2f4a8f; box-shadow: 0 10px 28px rgba(0, 0, 0, 0.5); animation: bc-in 0.45s cubic-bezier(0.3, 1.5, 0.5, 1); }
      .bc--left .bc-bubble { border-radius: 18px 18px 18px 6px; }
      @keyframes bc-in { 0% { transform: translateY(14px) scale(0.85); opacity: 0; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
      .bc-bubble p { margin: 0; font-size: 0.8rem; line-height: 1.4; }
      .bc-x { position: absolute; top: 4px; right: 6px; width: 24px; height: 24px; border: 0; border-radius: 50%; font-size: 1.1rem; line-height: 1; color: #cbd5e1; background: transparent; cursor: pointer; }
      .bc-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
      .bc-act { padding: 6px 12px; border: 1px solid #2f4a8f; border-radius: 999px; font-size: 0.72rem; font-weight: 700; color: #e2e8f0; background: transparent; cursor: pointer; }
      .bc-act--main { color: #0b131e; background: linear-gradient(135deg, #a6ff00, #10b981); border-color: transparent; }
      .bc-menu { display: flex; flex-direction: column; min-width: 210px; padding: 6px; border-radius: 18px; background: #0f1d45; border: 1px solid #2f4a8f; box-shadow: 0 10px 28px rgba(0, 0, 0, 0.5); animation: bc-in 0.3s ease-out; }
      .bc-menu button { padding: 10px 12px; border: 0; border-radius: 12px; font-size: 0.8rem; font-weight: 700; text-align: left; color: #e2e8f0; background: transparent; cursor: pointer; }
      .bc-menu button:active { background: rgba(166, 255, 0, 0.14); }
      .bc-menu-bar { display: flex; justify-content: space-between; gap: 6px; margin-bottom: 4px; padding: 2px 2px 8px; border-bottom: 1px solid rgba(255, 255, 255, 0.1); }
      .bc-menu .bc-ico { display: flex; align-items: center; justify-content: center; flex: 1; height: 40px; padding: 0; border-radius: 14px; font-size: 1.05rem; text-align: center; color: #e2e8f0; background: rgba(255, 255, 255, 0.07); }
      .bc-menu .bc-ico--off { color: #fca5a5; background: rgba(239, 68, 68, 0.18); }
      @media (prefers-reduced-motion: reduce) { .bc-bubble, .bc-menu, .bc-dot { animation: none; } }
    `
  ]
})
export class BreakyCompanionComponent implements OnInit, OnDestroy {
  readonly companion = inject(BreakyCompanionService);
  private readonly wardrobe = inject(BreakyWardrobeService);
  private readonly router = inject(Router);
  private readonly tour = inject(TourService);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly size = SIZE;
  readonly faRobot = faRobot;
  readonly faBell = faBell;
  readonly faBellSlash = faBellSlash;
  readonly outfit$ = this.wardrobe.outfit$;

  shown = true;
  menuOpen = false;
  side: 'left' | 'right' = 'right';
  bottom = 16;

  dragging = false;
  dragDx = 0;
  dragDy = 0;
  private startX = 0;
  private startY = 0;
  private startAt = 0;
  private moved = false;
  private hasMessage = false;
  private sub?: Subscription;
  private msgSub?: Subscription;

  ngOnInit(): void {
    this.restorePos();
    this.updateShown(this.router.url);
    this.msgSub = this.companion.message$.subscribe((m) => (this.hasMessage = !!m));
    this.sub = this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      this.menuOpen = false;
      this.updateShown(e.urlAfterRedirects);
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.msgSub?.unsubscribe();
  }

  private updateShown(url: string): void {
    this.shown = !isCompanionHidden(url);
  }

  // ---------- arrastrar y tocar ----------

  down(e: PointerEvent): void {
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startAt = Date.now();
    this.moved = false;
    this.dragDx = 0;
    this.dragDy = 0;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    this.companion.noteActivity();
  }

  move(e: PointerEvent): void {
    if (!(e.buttons & 1) && e.pointerType === 'mouse') return;
    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;
    if (!this.moved && Math.hypot(dx, dy) < 8) return;
    this.moved = true;
    this.dragging = true;
    this.menuOpen = false;
    this.dragDx = dx;
    this.dragDy = dy;
  }

  up(e: PointerEvent): void {
    if (this.moved) {
      // Suelta: se pega al borde mas cercano y se limita la altura.
      this.side = e.clientX < window.innerWidth / 2 ? 'left' : 'right';
      const nextBottom = this.bottom - this.dragDy;
      this.bottom = Math.min(window.innerHeight - SIZE - 70, Math.max(16, Math.round(nextBottom)));
      this.dragging = false;
      this.dragDx = 0;
      this.dragDy = 0;
      this.savePos();
      return;
    }
    if (Date.now() - this.startAt < 600) this.tap();
  }

  cancel(): void {
    this.dragging = false;
    this.dragDx = 0;
    this.dragDy = 0;
  }

  /** Toque: si hay mensaje lo cierra; si no, abre o cierra el menu rapido. */
  private tap(): void {
    if (this.hasMessage) {
      this.companion.dismiss();
      return;
    }
    this.menuOpen = !this.menuOpen;
  }

  // ---------- acciones ----------

  act(a: CompanionAction): void {
    this.companion.dismiss();
    if (a.route) void this.navigate(a.route);
    if (a.tour) void this.tour.start(a.tour as TourId);
  }

  go(route: string): void {
    this.menuOpen = false;
    void this.navigate(route);
  }

  tip(): void {
    this.menuOpen = false;
    this.companion.tipNow();
  }

  toggleMessages(): void {
    this.companion.setPrefs({ messages: !this.companion.prefs.messages });
  }

  private navigate(route: string): Promise<boolean> {
    const [path, query] = route.split('?');
    const queryParams = query ? Object.fromEntries(new URLSearchParams(query)) : undefined;
    return this.router.navigate([path], { queryParams });
  }

  @HostListener('document:pointerdown', ['$event'])
  closeMenuOutside(e: Event): void {
    if (this.menuOpen && !(this.host.nativeElement as HTMLElement).contains(e.target as Node)) this.menuOpen = false;
  }

  // ---------- posicion ----------

  private restorePos(): void {
    try {
      const raw = JSON.parse(localStorage.getItem(POS_KEY) ?? 'null');
      if (raw?.side === 'left' || raw?.side === 'right') this.side = raw.side;
      if (typeof raw?.bottom === 'number') this.bottom = raw.bottom;
    } catch {
      // posicion por defecto
    }
  }

  private savePos(): void {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify({ side: this.side, bottom: this.bottom }));
    } catch {
      // sin almacenamiento
    }
  }
}
